import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  CircularProgress,
  TablePagination,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import axios from "../../utils/axiosInstance";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { useNavigate, useLocation } from "react-router-dom";
import AddUnitForm from "./AddUnitForm";
import ConfirmDialog from "../ConfirmDialog";
import EditUnitForm from "./EditUnitForm";
import { toast } from "react-toastify";
import { formatReadableDate } from "../../utils/formatDate";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Collapse } from "@mui/material";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function UnitSearch({ onUnitChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [units, setUnits] = useState([]);
  const [query, setQuery] = useState("");
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [loadingUnitSearchData, setLoadingUnitSearchData] = useState(true);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Export menu state
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [deleting, setDeleting] = useState(false);

  const [showAssignDialog, setShowAssignDialog] = useState(false);

  const [expandedRows, setExpandedRows] = useState({});

  const sortUnits = (unitsToSort) => {
    if (!sortConfig.key) return unitsToSort;

    return [...unitsToSort].sort((a, b) => {
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";

      if (typeof aValue === "string") {
        if (sortConfig.direction === "asc") return aValue.localeCompare(bValue);
        else return bValue.localeCompare(aValue);
      } else if (typeof aValue === "number") {
        if (sortConfig.direction === "asc") return aValue - bValue;
        else return bValue - aValue;
      } else {
        return 0;
      }
    });
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const fetchUnits = async () => {
    try {
      setLoadingUnitSearchData(true);
      let Api_return = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/alldeviceslatlon?companyId=${process.env.REACT_APP_COMPANY_ID}`,
      );
      const data = Api_return.data;
      let formatted = await data.map((unit) => {
        let lastContact = null;
        if (unit.locationTime) {
          lastContact = new Date(unit.locationTime * 1000).toISOString();
        }
        return {
          id: unit.id,
          name: unit.name,
          lastContact: lastContact ? formatReadableDate(lastContact) : "N/A",
          // latitude: unit.latitude,
          // longitude: unit.longitude,
          macaddr: unit.macaddr,
          groupId: unit.groupId,
        };
      });

      setUnits(formatted);
      setFilteredUnits(formatted);
    } catch (err) {
      console.error("Error fetching devices:", err);
    } finally {
      setLoadingUnitSearchData(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, []);
  const handleSearch = (e) => {
    const q = e.target.value; // keep original casing
    setQuery(q); // display whatever user types
    const filtered = units.filter((u) => {
      const name = u.name || "";
      const id = u.id || "";
      return (
        name.toLowerCase().includes(q.toLowerCase()) ||
        id.toLowerCase().includes(q.toLowerCase())
      );
    });
    setFilteredUnits(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const refreshUnits = () => {
    fetchUnits();
  };

  const addUnit = () => {
    setShowAddForm(true);
  };

  const editUnit = (unit) => {
    setSelectedUnit(unit);
    setShowEditForm(true);
  };

  const handleAssign = () => {
    setShowAssignDialog(true);
  };

  const handleCloseAssignDialog = () => {
    setShowAssignDialog(false);
  };

  const handleFormSubmit = (apiResponse) => {
    if (!apiResponse) return;
    let device = apiResponse.device;

    const newUnit = {
      id: device.id,
      name: device.name,
      lastContact: "No Contact",
      latitude: device.latitude || "0",
      macaddr: device.macaddr,
      groupId: device.groupId,
    };

    setUnits((prev) => [...prev, newUnit]);
    setFilteredUnits((prev) => [...prev, newUnit]);
    setShowAddForm(false);
    // Refresh sidebar
  };

  const handleFormEdit = (updatedUnit) => {
    if (!updatedUnit) return;

    const formattedUpdatedUnit = {
      id: updatedUnit.id,
      name: updatedUnit.name,
      lastContact: updatedUnit.lastContact || "No Contact",
      latitude: updatedUnit.latitude,
      macaddr: updatedUnit.macaddr,
      groupId: updatedUnit.groupId,
    };

    setUnits((prevUnits) => {
      const unitExists = prevUnits.some((unit) => unit.id === updatedUnit.id);
      if (!unitExists) {
        return [...prevUnits, formattedUpdatedUnit];
      }
      return prevUnits.map((unit) =>
        unit.id === updatedUnit.id ? formattedUpdatedUnit : unit,
      );
    });

    setFilteredUnits((prevFiltered) => {
      const searchTerm = query.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        formattedUpdatedUnit?.name?.toLowerCase().includes(searchTerm) ||
        formattedUpdatedUnit?.id?.toLowerCase().includes(searchTerm);

      const unitExistsInFiltered = prevFiltered.some(
        (unit) => unit.id === updatedUnit.id,
      );

      if (!unitExistsInFiltered && matchesSearch) {
        return [...prevFiltered, formattedUpdatedUnit];
      } else if (unitExistsInFiltered && !matchesSearch) {
        return prevFiltered.filter((unit) => unit.id !== updatedUnit.id);
      } else if (unitExistsInFiltered && matchesSearch) {
        return prevFiltered.map((unit) =>
          unit.id === updatedUnit.id ? formattedUpdatedUnit : unit,
        );
      }
      return prevFiltered;
    });

    setShowEditForm(false);
    setSelectedUnit(null);
  };

  const gotoUnitPage = (id) => {
    navigate(`../device/${id}/engine-data`);
  };

  const gotoUnitInformationPage = (id) => {
    navigate(`../device/${id}/unit-information`);
  };

  const handleDeleteClick = (unit) => {
    setSelectedUnit(unit);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      await axios.delete(
        `${process.env.REACT_APP_API_GATEAWAY}/devices/${selectedUnit.id}`,
        {
          data: { UI_Description: `${getUIDescription()}\n Delete Device`,  },
        },
      );

      setUnits((prevUnits) =>
        prevUnits.filter((unit) => unit.id !== selectedUnit.id),
      );
      setFilteredUnits((prevFiltered) =>
        prevFiltered.filter((unit) => unit.id !== selectedUnit.id),
      );
      toast.success("Unit deleted successfully");
      if (onUnitChange) {
        console.log("first call");
        onUnitChange();
      }
    } catch (error) {
      console.error(
        "❌ Error deleting unit:",
        error.response?.data || error.message,
      );
      toast.error("Failed to delete unit");
    } finally {
      setConfirmOpen(false);
      setSelectedUnit(null);
      setDeleting(false);
    }
  };

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const exportToCSV = () => {
    const csvContent = [
      ["Unit Name", "Modem Serial Number", "Last Contact"],
      ...sortedUnits.map((unit) => [unit.name, unit.id, unit.lastContact]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `units_Management_data.csv`;
    link.click();

    handleExportMenuClose();
    toast.success(`Exported units Management data view to CSV`);
  };

  const exportToExcel = () => {
    let headers, rows;
    headers = ["Unit Name", "Modem Serial Number", "Last Contact"];
    rows = sortedUnits.map((unit) => [unit.name, unit.id, unit.lastContact]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Units");
    XLSX.writeFile(workbook, `units_Management_data.xlsx`);

    handleExportMenuClose();
    toast.success(`Exported units Management data view to Excel`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Units Report", 14, 20);

    let headers, rows;

    headers = [["Unit Name", "Modem Serial Number", "Last Contact"]];
    rows = sortedUnits.map((unit) => [unit.name, unit.id, unit.lastContact]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 30,
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`units_Management_data.pdf`);
    handleExportMenuClose();
    toast.success(`Exported units Management data view to PDF`);
  };

  const copyToClipboard = () => {
    const textContent = sortedUnits
      .map((unit) => `${unit.name}\t${unit.id}\t${unit.lastContact}`)
      .join("\n");

    const headers = "Unit Name\tModem Serial Number\tLast Contact";

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => toast.success("Data copied to clipboard"))
      .catch((err) => console.error("Failed to copy:", err));

    handleExportMenuClose();
  };

  const sortedUnits = sortUnits(filteredUnits);
  const paginatedUnits = sortedUnits.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const renderTable = () => (
    <TableContainer
      sx={{
        flexGrow: 1,
        height: { xs: "58vh", md: "59.7vh" },
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorY: "none", // Critical: stops body bounce
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
            {/* Mobile expand button column */}
            <TableCell
              sx={{
                display: { xs: "table-cell", md: "none" },
                fontWeight: "bold",
                textAlign: "center",
                fontSize: "12px",
                padding: "12px 8px",
                width: "50px",
              }}
            >
              Details
            </TableCell>
            <TableCell
              sx={{ fontWeight: "bold", cursor: "pointer" }}
              onClick={() => handleSort("name")}
            >
              Unit Name{" "}
              {sortConfig.key === "name"
                ? sortConfig.direction === "asc"
                  ? "▲"
                  : "▼"
                : ""}
            </TableCell>
            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                textAlign: "center",
                padding: "8px", // Explicitly control padding
              }}
            >
              Modem Serial Number
            </TableCell>

            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                textAlign: "center",
                padding: "8px", // Explicitly control padding
              }}
            >
              Last Contact
            </TableCell>
            <TableCell
              sx={{
                // display: { xs: "none", md: "table-cell" },
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                textAlign: "center",
                padding: "8px", // Explicitly control padding
              }}
            >
              View
            </TableCell>
            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                textAlign: "center",
                padding: "8px", // Explicitly control padding
              }}
            >
              Assign
            </TableCell>
            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                textAlign: "center",
                padding: "8px", // Explicitly control padding
              }}
            >
              Edit Unit
            </TableCell>
            <TableCell
              sx={{
                display: { xs: "none", md: "table-cell" },
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
                textAlign: "center",
                padding: "8px", // Explicitly control padding
              }}
            >
              Delete Unit
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedUnits.length > 0 ? (
            paginatedUnits.map((unit, index) => (
              <React.Fragment key={unit.id}>
                <TableRow
                  key={unit.id}
                  hover
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    "&:hover": {
                      backgroundColor: "#4a4d54",
                    },
                  }}
                >
                  {/* Expand Button - Mobile Only */}
                  <TableCell
                    sx={{
                      display: { xs: "table-cell", md: "none" },
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(unit.id)}
                      sx={{ color: "white" }}
                    >
                      {expandedRows[unit.id] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {unit.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      textAlign: "center",
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {unit.id}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      textAlign: "center",
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    }}
                  >
                    {unit.lastContact}
                  </TableCell>
                  <TableCell>
                    <Button
                      sx={{
                        borderRadius: 0,
                        textAlign: "center",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        // display: { xs: "none", md: "table-cell" },
                      }}
                      variant="contained"
                      size="small"
                      onClick={() => gotoUnitPage(unit.id)}
                    >
                      View
                    </Button>
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                      display: { xs: "none", md: "table-cell" }, // ADD THIS LINE
                    }}
                  >
                    <Button
                      sx={{
                        borderRadius: 0,
                        textAlign: "center",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        display: { xs: "none", md: "table-cell" },
                      }}
                      variant="contained"
                      size="small"
                      onClick={() => gotoUnitInformationPage(unit.id)}
                      // startIcon={<AssignmentIcon />}
                    >
                      Assign
                    </Button>
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                      display: { xs: "none", md: "table-cell" }, // ADD THIS LINE
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      sx={{
                        borderRadius: 0,
                        textAlign: "center",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        display: { xs: "none", md: "table-cell" },
                      }}
                      onClick={() => editUnit(unit)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: { xs: "11px", sm: "14px", md: "15px" },
                      display: { xs: "none", md: "table-cell" }, // ADD THIS LINE
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      sx={{
                        borderRadius: 0,
                        textAlign: "center",
                        fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        display: { xs: "none", md: "table-cell" },
                      }}
                      onClick={() => handleDeleteClick(unit)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={3}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    }}
                  >
                    <Collapse
                      in={expandedRows[unit.id]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ py: 2, px: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                          }}
                        >
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Modem Serial Number:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {unit.id}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Last Contact:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {unit.lastContact}
                            </Typography>
                          </Box>

                          {/* <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              View:
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              <Button
                                sx={{
                                  textAlign: "center",
                                  fontSize: {
                                    xs: "11px",
                                    sm: "14px",
                                    md: "15px",
                                  },
                                }}
                                variant="contained"
                                size="small"
                                onClick={() => gotoUnitPage(unit.id)}
                              >
                                View
                              </Button>
                            </Box>
                          </Box> */}

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Assign:
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              <Button
                                sx={{
                                  borderRadius: 0,
                                  textAlign: "center",
                                  fontSize: {
                                    xs: "11px",
                                    sm: "14px",
                                    md: "15px",
                                  },
                                }}
                                variant="contained"
                                size="small"
                                onClick={() => gotoUnitInformationPage(unit.id)}
                                // startIcon={<AssignmentIcon />}
                              >
                                Assign
                              </Button>
                            </Box>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Edit Unit:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                sx={{
                                  borderRadius: 0,
                                  textAlign: "center",
                                  fontSize: {
                                    xs: "11px",
                                    sm: "14px",
                                    md: "15px",
                                  },
                                }}
                                onClick={() => editUnit(unit)}
                              >
                                Edit
                              </Button>
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              Delete Unit:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                sx={{
                                  borderRadius: 0,
                                  textAlign: "center",
                                  fontSize: {
                                    xs: "11px",
                                    sm: "14px",
                                    md: "15px",
                                  },
                                }}
                                onClick={() => handleDeleteClick(unit)}
                              >
                                Delete
                              </Button>
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                No units found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 0, sm: 0 },
        height: "100%",
        position: "relative",
      }}
    >
      {loadingUnitSearchData && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(187, 183, 183, 0.12)",
            backdropFilter: "blur(10px)",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress color="#60a5fa" size={70} />
        </Box>
      )}

      <Paper
        elevation={4}
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              fontSize={{ xs: "1.15rem", sm: "2rem", md: "2rem" }}
              fontWeight="bold"
            >
              Unit Search & Management
            </Typography>
            <Chip
              label={`${filteredUnits.length} units`}
              color="primary"
              size="small"
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              width: { xs: "100%", sm: "unset", sm: "unset" },
              "& > button": {
                flex: { xs: 1, sm: "unset" },
              },
            }}
          >
            <Button
              variant="contained"
              sx={{
                borderRadius: 0,
                bgcolor: "#00BFFF",
                "&:hover": { bgcolor: "#18a5d4ff" },
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
              }}
              onClick={refreshUnits}
            >
              Refresh Units
            </Button>

            <Button
              variant="outlined"
              sx={{
                borderRadius: 0,
                fontWeight: "bold",
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
              }}
              onClick={addUnit}
            >
              Add Unit
            </Button>

            <Button
              color="white"
              variant="outlined"
              startIcon={<GetAppIcon />}
              sx={{
                borderRadius: 0,
                fontSize: { xs: "11px", sm: "14px", md: "15px" },
              }}
              onClick={handleExportMenuOpen}
            >
              Export
            </Button>
          </Box>
        </Box>

        <TextField
          label="Search by Name or ID"
          variant="outlined"
          value={query}
          onChange={handleSearch}
          fullWidth
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 0, // ✅ correct target
            },
          }}
        />

        {renderTable()}

        <TablePagination
          component="div"
          count={filteredUnits.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          sx={{ mt: 2 }}
        />

        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={handleExportMenuClose}
        >
          <MenuItem onClick={copyToClipboard}>
            <ListItemIcon>
              <FileCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToCSV}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>CSV</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToExcel}>
            <ListItemIcon>
              <TableViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Excel</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToPDF}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>PDF</ListItemText>
          </MenuItem>
        </Menu>
      </Paper>

      <AddUnitForm
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
        onSubmit={handleFormSubmit}
      />

      <EditUnitForm
        open={showEditForm}
        onClose={() => setShowEditForm(false)}
        onSubmit={handleFormEdit}
        unitData={selectedUnit}
        onUpdateSuccess={fetchUnits}
      />

      <Dialog
        open={showAssignDialog}
        onClose={handleCloseAssignDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "80vh",
            maxHeight: "800px",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Assign Device
              {/* {group?.name || "ROOT GROUP"} */}
            </Typography>
            <IconButton onClick={handleCloseAssignDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {/* <AssigningCustomerGroupDevice
                  devices={filteredDisplayUnits} // Currently selected/filtered devices
                  allDevicesss={availableDevices} // Devices NOT currently selected
                  members={units}
                  group={group}
                  onRecipientsUpdated={() => {
      
                    setShowAssignDialog(false);
                    setHandleRefresh(!handleRefresh);
      
                  }}
                /> */}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </Box>
  );
}
